import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiUpload,
  FiDownload,
  FiFile,
  FiUsers,
  FiChevronLeft,
  FiChevronsLeft,
  FiChevronRight,
  FiChevronsRight,
  FiSearch,
  FiRefreshCw,
  FiX,
  FiPhone,
  FiCheckCircle,
  FiClock,
} from "react-icons/fi";
import {
  uploadBeneficiaries,
  downloadTemplate,
  fetchBeneficiaries,
} from "../../../redux/actions/survey-management/beneficiariesActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  fetchWards,
  clearMunicipalities,
  clearWards,
} from "../../../redux/actions/internal/internalActions";

const Beneficiaries = () => {
  const dispatch = useDispatch();
  const { loading, beneficiaries, pagination, uploadProgress, surveyStats } =
    useSelector(
      (state) =>
        state.beneficiaries || {
          loading: false,
          beneficiaries: [],
          pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
          uploadProgress: 0,
          surveyStats: { totalSurvey: 0, surveyedCount: 0, pendingCount: 0 },
        },
    );

  const { districts, municipalities, wards } = useSelector(
    (state) =>
      state.internal || { districts: [], municipalities: [], wards: [] },
  );

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isBulkExporting, setIsBulkExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
    ward_id: "",
    ward_name: "",
    search: "",
    survey_completed: "",
  });
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    dispatch(fetchBeneficiaries(currentPage, limit, filters));
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
    filters.ward_name,
    filters.search,
    filters.survey_completed,
  ]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
      dispatch(clearWards());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (filters.municipality_id) {
      dispatch(fetchWards(filters.municipality_id));
    } else {
      dispatch(clearWards());
    }
  }, [filters.municipality_id, dispatch]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        setSelectedFile(null);
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const result = await dispatch(uploadBeneficiaries(formData));
      if (result?.meta?.status) {
        toast.success(
          result.meta.message || "Beneficiaries uploaded successfully!",
        );
        setSelectedFile(null);
        document.getElementById("fileInput").value = "";
        setShowUploadModal(false);
        dispatch(fetchBeneficiaries(currentPage, limit, filters));
      } else {
        toast.error(result?.meta?.message || "Upload failed");
      }
    } catch (error) {
      toast.error(error.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadTemplate());
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchBeneficiaries(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchBeneficiaries(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleExportToExcel = async () => {
    if (beneficiaries.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = [
      "Sl. no.",
      "ID",
      "Applicant Name",
      "Address",
      "Pincode",
      "Scheme",
      "Age",
      "Gender",
      "Caste",
      "Father's Name",
      "Mobile No.",
      "District",
      "Municipality",
      "Ward",
      "Survey Completed",
      "BPL ID",
      "BPL Member ID",
      "Sanction Order No.",
      "Ration Card No.",
      "EPIC No.",
      "Beneficiary No.",
      "DLS Status",
      "DLC Expiry Date",
      "Updated Date",
    ];

    // Map data to rows
    const rows = beneficiaries.map((item, index) => {
      return [
        { value: index + 1, changed: false },
        { value: item.id || "N/A", changed: false },
        { value: item.applicant_name || "N/A", changed: false },
        { value: item.address || "N/A", changed: false },
        { value: item.pincode || "N/A", changed: false },
        { value: item.scheme || "N/A", changed: false },
        { value: item.age || "N/A", changed: false },
        { value: item.gender || "N/A", changed: false },
        { value: item.caste || "N/A", changed: false },
        { value: item.fh_name || "N/A", changed: false },
        { value: item.mobile_no || "N/A", changed: false },
        { value: item.district_name || "N/A", changed: false },
        { value: item.municipality_name || "N/A", changed: false },
        { value: item.ward || "N/A", changed: false },
        {
          value: item.survey_completed === 1 ? "Completed" : "Pending",
          changed: false,
        },
        { value: item.bpl_id || "N/A", changed: false },
        { value: item.bpl_member_id || "N/A", changed: false },
        { value: item.sanction_order_no || "N/A", changed: false },
        { value: item.ration_card_no || "N/A", changed: false },
        { value: item.epic_no || "N/A", changed: false },
        { value: item.beneficiary_no || "N/A", changed: false },
        {
          value:
            item.dls_status === "Yes" || item.dls_status === "Active"
              ? "Active"
              : "Inactive",
          changed: false,
        },
        {
          value: item.dlc_expired_date
            ? new Date(item.dlc_expired_date).toLocaleDateString("en-IN")
            : "N/A",
          changed: false,
        },
        {
          value: item.updated_at
            ? new Date(item.updated_at).toLocaleDateString("en-IN")
            : "N/A",
          changed: false,
        },
      ];
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Beneficiaries");

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFB923C" },
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    rows.forEach((rowData) => {
      const row = worksheet.addRow(rowData.map((cell) => cell.value));

      row.eachCell((cell, colNumber) => {
        const cellIndex = colNumber - 1;
        const cellData = rowData[cellIndex];

        if (cellData && cellData.changed) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFCCCC" },
          };
          cell.font = {
            color: { argb: "FF000000" },
            bold: true,
          };
        }

        cell.border = {
          top: { style: "thin", color: { argb: "FFD3D3D3" } },
          left: { style: "thin", color: { argb: "FFD3D3D3" } },
          bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
          right: { style: "thin", color: { argb: "FFD3D3D3" } },
        };

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });
    });

    // Set column widths
    const colWidths = [
      8, 8, 25, 30, 12, 15, 8, 10, 12, 20, 15, 20, 25, 10, 18, 15, 18, 22, 18,
      20, 18, 15, 18, 18,
    ];

    worksheet.columns = colWidths.map((width) => ({
      width: width,
    }));

    // Freeze header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const date = new Date();
    const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}-${date.getFullYear()}`;

    saveAs(new Blob([buffer]), `Beneficiaries_${dateStr}.xlsx`);
    toast.success("Export successful!");
  };

  const handleBulkExportToExcel = async () => {
    setIsBulkExporting(true);
    setExportProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setExportProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          const increment = Math.random() < 0.3 ? 1 : 0;
          return Math.min(prev + increment, 95);
        });
      }, 500);

      toast.loading("Fetching all records...", {
        id: "bulk-export-beneficiaries",
      });

      const { fetchAllBeneficiaries } =
        await import("../../../redux/actions/survey-management/beneficiariesActions");

      const allBeneficiaries = await fetchAllBeneficiaries(filters, 2000000);

      clearInterval(progressInterval);
      setExportProgress(100);

      if (allBeneficiaries.length === 0) {
        toast.dismiss("bulk-export-beneficiaries");
        toast.error("No data to export");
        setIsBulkExporting(false);
        setExportProgress(0);
        return;
      }

      const headers = [
        "Sl. no.",
        "ID",
        "Applicant Name",
        "Address",
        "Pincode",
        "Scheme",
        "Age",
        "Gender",
        "Caste",
        "Father's Name",
        "Mobile No.",
        "District",
        "Municipality",
        "Ward",
        "Survey Completed",
        "BPL ID",
        "BPL Member ID",
        "Sanction Order No.",
        "Ration Card No.",
        "EPIC No.",
        "Beneficiary No.",
        "DLS Status",
        "DLC Expiry Date",
        "Updated Date",
      ];

      const rows = allBeneficiaries.map((item, index) => {
        return [
          { value: index + 1, changed: false },
          { value: item.id || "N/A", changed: false },
          { value: item.applicant_name || "N/A", changed: false },
          { value: item.address || "N/A", changed: false },
          { value: item.pincode || "N/A", changed: false },
          { value: item.scheme || "N/A", changed: false },
          { value: item.age || "N/A", changed: false },
          { value: item.gender || "N/A", changed: false },
          { value: item.caste || "N/A", changed: false },
          { value: item.fh_name || "N/A", changed: false },
          { value: item.mobile_no || "N/A", changed: false },
          { value: item.district_name || "N/A", changed: false },
          { value: item.municipality_name || "N/A", changed: false },
          { value: item.ward || "N/A", changed: false },
          {
            value: item.survey_completed === 1 ? "Completed" : "Pending",
            changed: false,
          },
          { value: item.bpl_id || "N/A", changed: false },
          { value: item.bpl_member_id || "N/A", changed: false },
          { value: item.sanction_order_no || "N/A", changed: false },
          { value: item.ration_card_no || "N/A", changed: false },
          { value: item.epic_no || "N/A", changed: false },
          { value: item.beneficiary_no || "N/A", changed: false },
          {
            value:
              item.dls_status === "Yes" || item.dls_status === "Active"
                ? "Active"
                : "Inactive",
            changed: false,
          },
          {
            value: item.dlc_expired_date
              ? new Date(item.dlc_expired_date).toLocaleDateString("en-IN")
              : "N/A",
            changed: false,
          },
          {
            value: item.updated_at
              ? new Date(item.updated_at).toLocaleDateString("en-IN")
              : "N/A",
            changed: false,
          },
        ];
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Beneficiaries");

      // Add header row with styling
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFB923C" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add data rows
      rows.forEach((rowData) => {
        const row = worksheet.addRow(rowData.map((cell) => cell.value));

        row.eachCell((cell, colNumber) => {
          const cellIndex = colNumber - 1;
          const cellData = rowData[cellIndex];

          if (cellData && cellData.changed) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFCCCC" },
            };
            cell.font = {
              color: { argb: "FF000000" },
              bold: true,
            };
          }

          cell.border = {
            top: { style: "thin", color: { argb: "FFD3D3D3" } },
            left: { style: "thin", color: { argb: "FFD3D3D3" } },
            bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
            right: { style: "thin", color: { argb: "FFD3D3D3" } },
          };

          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
        });
      });

      const colWidths = [
        8, 8, 25, 30, 12, 15, 8, 10, 12, 20, 15, 20, 25, 10, 18, 15, 18, 22, 18,
        20, 18, 15, 18, 18,
      ];

      worksheet.columns = colWidths.map((width) => ({
        width: width,
      }));

      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${date.getFullYear()}`;

      saveAs(new Blob([buffer]), `Beneficiaries_${dateStr}.xlsx`);
      toast.success("Export successful!", {
        id: "bulk-export-beneficiaries",
      });
    } catch (error) {
      toast.error("Failed to export", {
        id: "bulk-export-beneficiaries",
      });
      console.error("Bulk export error:", error);
    } finally {
      setTimeout(() => {
        setIsBulkExporting(false);
        setExportProgress(0);
      }, 1000);
    }
  };
  const filteredBeneficiaries = beneficiaries;

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            Beneficiaries
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage and upload beneficiary data
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Upload Button */}
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <FiUpload className="w-4 h-4" />
            Upload Beneficiaries
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Download Template
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleBulkExportToExcel}
            disabled={isBulkExporting}
            className="relative flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden min-w-[180px]"
          >
            {isBulkExporting && (
              <div
                className="absolute left-0 top-0 h-full bg-red-400/30 transition-all duration-300"
                style={{ width: `${exportProgress}%` }}
              />
            )}
            {isBulkExporting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white relative z-10"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="relative z-10">
                  Exporting {exportProgress}%
                </span>
              </>
            ) : (
              <>
                <FiDownload className="w-4 h-4 relative z-10" />
                <span className="relative z-10">Export All Records</span>
              </>
            )}
          </button>

          <button
            onClick={handleExportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Export to Excel
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <FiUsers className="w-5 h-5 text-gray-800 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Beneficiaries List
            </h2>
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-700 px-3 py-1 rounded-md">
              TOTAL: {pagination.totalRecords || 0}
            </span>
            <div className="flex items-center gap-2 ml-4">
              <label className="text-sm text-gray-800 dark:text-gray-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border-2 border-orange-200 dark:border-orange-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium min-w-[80px]"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* First Row - District and Municipality */}
          <div className="flex items-center gap-3 mb-3">
            <select
              value={filters.district_id}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  district_id: value,
                  municipality_id: "",
                  ward_id: "",
                  ward_name: "",
                  search: filters.search,
                  survey_completed: "",
                });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[180px]"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            <select
              value={filters.municipality_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  municipality_id: e.target.value,
                  ward_id: "",
                  ward_name: "",
                  survey_completed: "",
                });
                setCurrentPage(1);
              }}
              disabled={!filters.district_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[180px] disabled:opacity-50"
            >
              <option value="">All Municipalities</option>
              {municipalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <select
              value={filters.ward_id}
              onChange={(e) => {
                const wardId = e.target.value;
                const selectedWard = wards.find(
                  (w) => w.id === parseInt(wardId),
                );
                setFilters({
                  ...filters,
                  ward_id: wardId,
                  ward_name: selectedWard ? selectedWard.name : "",
                });
                setCurrentPage(1);
              }}
              disabled={!filters.municipality_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[150px] disabled:opacity-50"
            >
              <option value="">All Wards</option>
              {wards.map((w) => (
                <option key={w.id} value={w.id}>
                  Ward {w.name}
                </option>
              ))}
            </select>
          </div>

          {/* Second Row - Search and Clear Button */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by Name, Mobile, Sanction order number "
                value={filters.search}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-2 pl-9 border-2 border-orange-400 dark:border-orange-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-full min-w-[450px] transition-all duration-200"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <FiSearch className="w-4 h-4" />
              </span>
            </div>

            <select
              value={filters.survey_completed}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  survey_completed: e.target.value,
                });
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[160px]"
            >
              <option value="">All Status</option>
              <option value="1">Completed</option>
              <option value="0">Pending</option>
            </select>

            {(filters.district_id ||
              filters.municipality_id ||
              filters.search ||
              filters.survey_completed) && (
              <button
                onClick={() => {
                  setFilters({
                    district_id: "",
                    municipality_id: "",
                    ward_id: "",
                    ward_name: "",
                    search: "",
                    survey_completed: "",
                  });
                  setCurrentPage(1);
                  dispatch(clearMunicipalities());
                  dispatch(clearWards());
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm flex items-center gap-1"
              >
                <FiX className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
        {/* Pagination - Top */}
        {/* {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-800 dark:text-gray-400">
              Showing {(pagination.page - 1) * limit + 1} to{" "}
              {Math.min(pagination.page * limit, pagination.totalRecords)} of{" "}
              {pagination.totalRecords} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-800 dark:text-gray-400"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium">
                {currentPage}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-800 dark:text-gray-400"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )} */}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {/* Total Survey Card */}
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                  <FiUsers className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                </div>
                <div>
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                    Total Survey
                  </p>
                  <p className="text-lg font-bold text-blue-900 dark:text-white leading-tight">
                    {surveyStats.totalSurvey || 0}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                  All
                </span>
              </div>
            </div>
          </div>

          {/* Surveyed Card  */}
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-800/50 rounded-lg">
                  <FiCheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                </div>
                <div>
                  <p className="text-xs font-medium text-green-700 dark:text-green-300 uppercase tracking-wider">
                    Survey Count
                  </p>
                  <p className="text-lg font-bold text-green-700 dark:text-green-300 leading-tight">
                    {surveyStats.surveyedCount || 0}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs font-medium text-green-600 dark:text-green-400">
                  {surveyStats.totalSurvey > 0
                    ? `${Math.round((surveyStats.surveyedCount / surveyStats.totalSurvey) * 100)}%`
                    : "0%"}
                </span>
              </div>
            </div>
          </div>

          {/* Pending Card*/}
          <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-800 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-800/50 rounded-lg">
                  <FiClock className="w-4 h-4 text-yellow-600 dark:text-yellow-300" />
                </div>
                <div>
                  <p className="text-xs font-medium text-yellow-700 dark:text-yellow-300 uppercase tracking-wider">
                    Pending
                  </p>
                  <p className="text-lg font-bold text-yellow-700 dark:text-yellow-300 leading-tight">
                    {surveyStats.pendingCount || 0}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">
                  {surveyStats.totalSurvey > 0
                    ? `${Math.round((surveyStats.pendingCount / surveyStats.totalSurvey) * 100)}%`
                    : "0%"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Left side - Showing entries info */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {(pagination.page - 1) * pagination.limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {Math.min(
                  pagination.page * pagination.limit,
                  pagination.totalRecords,
                )}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {pagination.totalRecords}
              </span>{" "}
              entries
            </div>

            {/* Right side - Pagination controls */}
            <div className="flex items-center gap-2">
              {/* First Page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronsLeft className="w-4 h-4" />
              </button>

              {/* Previous */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1.5">
                {(() => {
                  const totalPages = pagination.totalPages;
                  const current = currentPage;
                  const pages = [];
                  const maxVisible = 5;

                  if (totalPages <= maxVisible) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    if (current <= 3) {
                      pages.push(1, 2, 3, 4, "...", totalPages);
                    } else if (current >= totalPages - 2) {
                      pages.push(
                        1,
                        "...",
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages,
                      );
                    } else {
                      pages.push(
                        1,
                        "...",
                        current - 1,
                        current,
                        current + 1,
                        "...",
                        totalPages,
                      );
                    }
                  }

                  return pages.map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-2 text-gray-500 dark:text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          current === page
                            ? "bg-orange-600 text-white shadow-md shadow-orange-500/30"
                            : "border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  );
                })()}
              </div>

              {/* Next */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>

              {/* Last Page */}
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredBeneficiaries.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/30">
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      #
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[150px]">
                      Applicant Name
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 min-w-[170px]">
                      Address
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400">
                      Pincode
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[150px]">
                      Father's Name
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Scheme
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Age/Gender
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Mobile
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Caste
                    </th>

                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[100px]">
                      District
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[100px]">
                      Municipality
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Ward
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Survey Completed
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400">
                      BPL ID
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 min-w-[110px]">
                      BPL Member ID
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2 text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[150px]">
                      Sanction Order
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 min-w-[130px]">
                      Ration Card No.
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400">
                      EPIC No.
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 ">
                      Beneficiary No.
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 ">
                      DLS Status
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 min-w-[100px]">
                      DLC Expiry
                    </th>
                    <th className="text-left uppercase italic text-base py-1.5 px-2 text-center text-xs font-medium text-orange-800 dark:text-orange-400 min-w-[100px]">
                      Updated At
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBeneficiaries.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-1.5 px-2 text-center text-xs text-gray-500 dark:text-gray-400">
                        {(pagination.page - 1) * limit + index + 1}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs font-medium text-gray-900 dark:text-white">
                        {item.applicant_name || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-left text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.address || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center font-mono text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.pincode || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.fh_name || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs">
                        <span className="px-2 py-1 text-center text-xs text-gray-800 dark:text-gray-300 text-xs">
                          {item.scheme || "N/A"}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.age || "N/A"} / {item.gender || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 font-mono dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.mobile_no ? (
                          <div className="flex items-center gap-1.5">
                            <FiPhone className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                            <span>{item.mobile_no}</span>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.caste || "N/A"}
                      </td>

                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.district_name || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.municipality_name || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.ward || "N/A"}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.survey_completed === 1
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                              : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                          }`}
                        >
                          {item.survey_completed === 1
                            ? "Completed"
                            : "Pending"}
                        </span>
                      </td>
                      <td className="text-center py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.bpl_id || "N/A"}
                      </td>
                      {/* BPL Member ID */}
                      <td className="text-center py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.bpl_member_id ? (
                          <span className="font-mono text-sm text-gray-700 dark:text-gray-300 ">
                            {item.bpl_member_id}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.sanction_order_no || "N/A"}
                      </td>
                      {/* Ration Card No. */}
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.ration_card_no ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 ">
                            {item.ration_card_no}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>

                      {/* EPIC No. */}
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.epic_no ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 ">
                            {item.epic_no}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>

                      {/* Beneficiary No. */}
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.beneficiary_no ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 ">
                            {item.beneficiary_no}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>

                      {/* DLS Status */}
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.dls_status ? (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.dls_status === "Yes" ||
                              item.dls_status === "Active"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                            }`}
                          >
                            {item.dls_status === "Yes" ||
                            item.dls_status === "Active"
                              ? "Active"
                              : "Inactive"}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>

                      {/* DLC Expiry */}
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.dlc_expired_date ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 ">
                            {new Date(item.dlc_expired_date).toLocaleDateString(
                              "en-IN",
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      {/* DLC Expiry */}
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.updated_at ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 ">
                            {new Date(item.updated_at).toLocaleDateString(
                              "en-IN",
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                {/* Left side - Showing entries info */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.totalRecords,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {pagination.totalRecords}
                  </span>{" "}
                  entries
                </div>

                {/* Right side - Pagination controls */}
                <div className="flex items-center gap-2">
                  {/* First Page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronsLeft className="w-4 h-4" />
                  </button>

                  {/* Previous */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const current = currentPage;
                      const pages = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        if (current <= 3) {
                          pages.push(1, 2, 3, 4, "...", totalPages);
                        } else if (current >= totalPages - 2) {
                          pages.push(
                            1,
                            "...",
                            totalPages - 3,
                            totalPages - 2,
                            totalPages - 1,
                            totalPages,
                          );
                        } else {
                          pages.push(
                            1,
                            "...",
                            current - 1,
                            current,
                            current + 1,
                            "...",
                            totalPages,
                          );
                        }
                      }

                      return pages.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-gray-500 dark:text-gray-400"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                              current === page
                                ? "bg-orange-600 text-white shadow-md shadow-orange-500/30"
                                : "border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      );
                    })()}
                  </div>

                  {/* Next */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No beneficiaries found
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Upload beneficiary data using the upload section above
            </p>
          </div>
        )}
      </div>
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Upload Beneficiaries
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("fileInput").value = "";
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-orange-500 dark:hover:border-orange-400 transition-colors">
              <input
                id="fileInput"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading || loading}
              />
              <label htmlFor="fileInput" className="cursor-pointer block">
                <div className="flex flex-col items-center">
                  <FiFile className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-3" />
                  <p className="text-gray-600 dark:text-gray-300 font-medium">
                    {selectedFile
                      ? selectedFile.name
                      : "Click to select Excel file"}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Supported formats: .xlsx, .xls
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>
              </label>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className="bg-orange-600 dark:bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-400 mt-1">
                  {uploadProgress}% uploaded
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("fileInput").value = "";
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading || loading}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Uploading...
                  </>
                ) : (
                  "Upload"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Beneficiaries;
